import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { clinicInfo } from '../mock';

import { CrisisBanner } from '../components/sections/CrisisBanner';
import { Header } from '../components/sections/Header';
import { HeroSection } from '../components/sections/HeroSection';
import { TrustBadges } from '../components/sections/TrustBadges';
import { SuccessMetrics } from '../components/sections/SuccessMetrics';
import { WhyChooseUs } from '../components/sections/WhyChooseUs';
import { ServicesSection } from '../components/sections/ServicesSection';
import { Testimonials } from '../components/sections/Testimonials';
import { EducationalVideos } from '../components/sections/EducationalVideos';
import { InsuranceSection } from '../components/sections/InsuranceSection';
import { CallToAction } from '../components/sections/CallToAction';
import { AppointmentBooking } from '../components/sections/AppointmentBooking';
import { WhatToExpect } from '../components/sections/WhatToExpect';
import { FAQSection } from '../components/sections/FAQSection';
import { OfficeHoursLocation } from '../components/sections/OfficeHoursLocation';
import { BlogResources } from '../components/sections/BlogResources';
import { ContactForm } from '../components/sections/ContactForm';
import { Footer } from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration constants
const STICKY_BUTTON_SCROLL_THRESHOLD = 800;
const FADE_IN_OBSERVER_THRESHOLD = 0.1;
const WATERMARK_SIZE = '180px 180px';
const WATERMARK_OPACITY = 0.03;

const INITIAL_FORM_STATE = {
  name: '',
  email: '',
  phone: '',
  message: '',
  preferredContact: 'email',
  preferredDate: '',
  preferredTime: ''
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showStickyButton, setShowStickyButton] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  // Scroll handler for sticky button
  useEffect(() => {
    const handleScroll = () => setShowStickyButton(window.scrollY > STICKY_BUTTON_SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer for fade-in animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: FADE_IN_OBSERVER_THRESHOLD }
    );
    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        toast.success('Message sent successfully! We will contact you soon.');
        setFormData(INITIAL_FORM_STATE);
      }
    } catch {
      toast.error('Failed to send message. Please try again or call us directly at ' + clinicInfo.phone);
    }
  }, [formData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-warmBeige to-white relative">
      {/* Watermark Background */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `url(${clinicInfo.logo})`,
          backgroundSize: WATERMARK_SIZE,
          backgroundRepeat: 'repeat',
          opacity: WATERMARK_OPACITY,
          backgroundPosition: 'center'
        }}
      />

      <CrisisBanner />
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrollToSection={scrollToSection} />

      {/* Sticky Book Appointment Button */}
      {showStickyButton && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 animate-in fade-in slide-in-from-bottom-4">
          <Button
            data-testid="sticky-book-btn"
            onClick={() => scrollToSection('appointment')}
            size="lg"
            className="bg-primaryOrange hover:bg-accentOrange text-white shadow-2xl rounded-full px-5 py-5 md:px-8 md:py-6 text-sm md:text-lg font-bold"
          >
            <Calendar className="mr-1 md:mr-2" size={20} />
            <span className="hidden sm:inline">Book Now</span>
            <span className="sm:hidden">Book</span>
          </Button>
        </div>
      )}

      <HeroSection scrollToSection={scrollToSection} />
      <TrustBadges />
      <SuccessMetrics />
      <WhyChooseUs />
      <ServicesSection />
      <Testimonials />
      <EducationalVideos />
      <InsuranceSection />
      <CallToAction scrollToSection={scrollToSection} />
      <AppointmentBooking />
      <WhatToExpect />
      <FAQSection />
      <OfficeHoursLocation />
      <BlogResources />
      <ContactForm formData={formData} handleInputChange={handleInputChange} handleSubmit={handleSubmit} />
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
};

export default Home;
